"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import dynamic from "next/dynamic";
import { CardDeck } from "@/components/scene/card-deck";
import { SceneLanguageToggle } from "@/components/scene/scene-language-toggle";
import { WorldLayers } from "@/components/scene/world-layers";
import { spawnEmbers } from "@/lib/scene/embers";

// Three.js scene is client-only (WebGL, window) — load without SSR.
const VolcanoStage = dynamic(
  () => import("@/components/scene/volcano-stage").then((m) => m.VolcanoStage),
  { ssr: false },
);

export function GardenScene() {
  const { t } = useTranslation();
  const appRef = useRef<HTMLElement>(null);
  const eruptRef = useRef(0);
  const chargeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [charging, setCharging] = useState(false);
  const [erupting, setErupting] = useState(false);
  const [modelReady, setModelReady] = useState(false);

  const burst = useCallback(() => {
    eruptRef.current = 1;
    setErupting(true);
    setCharging(true);
    window.setTimeout(() => {
      setErupting(false);
      setCharging(false);
    }, 980);
    if (appRef.current) spawnEmbers(appRef.current);
  }, []);

  // Pointer-following orb + proximity charge, mirrors the reference.
  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const host = appRef.current;
      if (!host) return;
      const dx = ((e.clientX - window.innerWidth / 2) / window.innerWidth) * 120;
      const dy =
        ((e.clientY - window.innerHeight * 0.44) / window.innerHeight) * 90;
      host.style.setProperty("--mx", `${dx}px`);
      host.style.setProperty("--my", `${dy}px`);
      const near =
        Math.hypot(
          e.clientX - window.innerWidth / 2,
          e.clientY - window.innerHeight * 0.52,
        ) < 120;
      if (near && !charging) {
        setCharging(true);
        if (chargeTimer.current) clearTimeout(chargeTimer.current);
        chargeTimer.current = setTimeout(() => setCharging(false), 800);
      }
      if (near && Math.random() < 0.035) burst();
    };
    window.addEventListener("pointermove", onMove);
    return () => {
      window.removeEventListener("pointermove", onMove);
      if (chargeTimer.current) clearTimeout(chargeTimer.current);
    };
  }, [charging, burst]);

  return (
    <main
      ref={appRef}
      className="relative isolate min-h-[100svh] overflow-hidden"
      style={{
        paddingTop: "var(--eazo-safe-area-top)",
        paddingBottom: "var(--eazo-safe-area-bottom)",
        background: "var(--scene-bg)",
        color: "var(--scene-text)",
      }}
      data-el="scene-shell"
    >
      <WorldLayers modelReady={modelReady} charging={charging}>
        <VolcanoStage
          eruptRef={eruptRef}
          onModelReady={() => setModelReady(true)}
        />
      </WorldLayers>

      {/* ===== Content ===== */}
      <section
        className="relative z-[2] grid max-w-[100vw] place-items-center px-[18px] pt-[18px] [grid-template-rows:1fr_auto]"
        style={{
          minHeight:
            "calc(100svh - var(--eazo-safe-area-top) - var(--eazo-safe-area-bottom))",
        }}
        aria-label={t("scene.kicker")}
      >
        <div
          className="mt-[4svh] w-full max-w-[640px] text-center"
          data-el="scene-hero"
        >
          <div
            className="mb-3 text-[11px] uppercase tracking-[0.22em] text-teal"
            style={{ textShadow: "0 0 20px rgba(126,216,176,.65)" }}
          >
            {t("scene.kicker")}
          </div>
          <h1
            className="mx-auto font-heading text-[clamp(34px,10vw,76px)] font-light leading-[1.04] tracking-[-0.02em]"
            style={{ textShadow: "0 0 30px rgba(244,255,248,.28)" }}
          >
            {t("scene.title")}
          </h1>
          <p className="mx-auto mt-3 max-w-[18em] text-[14px] leading-[1.5] text-scene-muted">
            {t("scene.sub")}
          </p>
          <button
            type="button"
            onClick={burst}
            data-el="ignite-button"
            className="mt-5 min-h-[46px] cursor-pointer rounded-full px-[22px] text-[#06120d] transition-transform duration-200 active:scale-95"
            style={{
              border: "1px solid rgba(255,255,255,.38)",
              background:
                "linear-gradient(135deg,rgba(244,255,248,.92),rgba(126,216,176,.72))",
              boxShadow:
                "0 0 34px rgba(126,216,176,.42), inset 0 1px 0 rgba(255,255,255,.85)",
            }}
          >
            {t("scene.ignite")}
          </button>
        </div>

        <CardDeck erupting={erupting} />
      </section>

      {/* Language toggle (replaces the ambient-sound corner control) */}
      <div
        className="absolute right-[18px] z-[5]"
        style={{ bottom: "calc(var(--eazo-safe-area-bottom) + 14px)" }}
      >
        <SceneLanguageToggle />
      </div>
    </main>
  );
}
